import React, { useState, useMemo, useEffect, useContext } from 'react'
import { readTheme, saveTheme } from '../theme/frost'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import styles from './StudentDashboard.module.scss'
import {
  STUDENT_LANGUAGES_QUERY, STUDENT_BRIEF_QUERY, STUDENT_WEAK_ITEMS_QUERY,
  STUDENT_LETTER_STATS_QUERY, STUDENT_DRILL_RESULTS_QUERY, STUDENT_TREE_QUERY,
  ASSIGN_LESSON_MUTATION,
} from '../query/gqlQueries'
import UserContext from '../context/UserContext'
import { topicLessonFromServer } from '../lessons/serverLesson'
import { DrillMisses, drillModeLabel } from './DrillDetails'

import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import StudentNotes from './StudentNotes'
import PhraseQuestions from './PhraseQuestions'

// One student, one language: what they have been doing, what they keep getting
// wrong, where they are on the tree, and one tap to set the next lesson.
//
// Built for the ten minutes before a lesson — the brief across the top should
// answer "where are they?" without any scrolling or reading.
//
// Everything here is refused by the server unless this student has added you
// with your teaching code, and it is deliberately narrow: learning data for one
// language. No profile facts, no journal, no notes, no other language.
// Day or night: the site's one choice (theme/frost.js) — the computer's until chosen.

const LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2']
const lvlIdx = (l) => { const i = LEVELS.indexOf(String(l || '').toUpperCase()); return i < 0 ? LEVELS.length : i }

// "today" / "yesterday" / "5 days ago" — a teacher wants the gap, not a date.
const sinceLabel = (iso) => {
  if (!iso) return 'never'
  const t = new Date(iso)
  if (isNaN(t)) return 'never'
  const days = Math.floor((Date.now() - t.getTime()) / 86400000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  const months = Math.round(days / 30)
  return months === 1 ? 'a month ago' : `${months} months ago`
}
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '')
const pctColor = (p) => (p >= 80 ? 'var(--accent, var(--frost-right))' : p >= 50 ? 'var(--amber, var(--frost-amber))' : 'var(--error, var(--frost-wrong))')
const STATUS_WORD = { assigned: 'not started', submitted: 'waiting for your feedback', reviewed: 'reviewed' }

export default function StudentDashboard({ student, onClose, onOpenImport }) {
  const [theme, setTheme] = useState(readTheme)
  useEffect(() => {
    const follow = (e) => setTheme(e.detail || readTheme())
    window.addEventListener('frost-theme', follow)
    return () => window.removeEventListener('frost-theme', follow)
  }, [])
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    saveTheme(next)
    return next
  })

  const [lang, setLang] = useState('')
  const [due, setDue] = useState('')
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const [assigning, setAssigning] = useState(null) // topic id being assigned
  const [openDrill, setOpenDrill] = useState(null) // shared set expanded to show what needed help
  const [treeLevel, setTreeLevel] = useState(null) // the level shown in their tree
  const [allWeak, setAllWeak] = useState(false)

  const langsQ = useQuery(STUDENT_LANGUAGES_QUERY, { variables: { student }, skip: !student, fetchPolicy: 'cache-and-network', errorPolicy: 'all' })
  const languages = useMemo(() => (langsQ.data?.studentLanguages || []).filter(l => l && l.code), [langsQ.data])
  // Default to the student's first active language, once they arrive.
  useEffect(() => {
    if (lang || languages.length === 0) return
    setLang((languages.find(l => l.active) || languages[0]).code)
  }, [languages, lang])

  const on = !!student && !!lang
  const briefQ = useQuery(STUDENT_BRIEF_QUERY, { variables: { student, lang }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'all' })
  const weakQ = useQuery(STUDENT_WEAK_ITEMS_QUERY, { variables: { student, lang, limit: 12 }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const lettersQ = useQuery(STUDENT_LETTER_STATS_QUERY, { variables: { student, lang }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const drillsQ = useQuery(STUDENT_DRILL_RESULTS_QUERY, { variables: { student, limit: 10 }, skip: !student, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const treeQ = useQuery(STUDENT_TREE_QUERY, { variables: { student, langCode: lang }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const [assign] = useMutation(ASSIGN_LESSON_MUTATION)
  const client = useApolloClient()
  const { username } = useContext(UserContext)
  const [building, setBuilding] = useState('')   // progress text while a lesson is written

  const brief = briefQ.data?.studentBrief
  const weak = weakQ.data?.studentWeakItems || []
  const letters = lettersQ.data?.studentLetterStats || []
  const drills = drillsQ.data?.studentDrillResults || []
  const topics = useMemo(() => treeQ.data?.studentTree || [], [treeQ.data])

  const doneSlugs = useMemo(() => new Set(topics.filter(t => t.completed).map(t => t.slug)), [topics])
  const byLevel = useMemo(() => {
    const groups = new Map()
    for (const t of topics) {
      const key = t.level || '—'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(t)
    }
    return [...groups.entries()].sort((a, b) => lvlIdx(a[0]) - lvlIdx(b[0]))
  }, [topics])
  const locked = (t) => (t.requires || []).some(r => !doneSlugs.has(r))

  // The server refuses outright when the teaching link is missing; say so
  // plainly rather than showing an empty dashboard that looks like no progress.
  const denied = [langsQ.error, briefQ.error].find(e => e && /only see the progress|not yours to see|has not yet said|Say which language|Not signed in/i.test(e.message || ''))
  const backendOld = /Cannot query field|Unknown type/i.test(langsQ.error?.message || '')

  const doAssign = async (topic) => {
    setMsg(''); setErr(''); setAssigning(topic.id)
    try {
      await assign({ variables: { meetingId: topic.lessonMeetingId, students: [student], due: due || null } })
      setMsg(`Assigned “${topic.title}” to ${student}${due ? ` · due ${fmtDate(due)}` : ''}.`)
      briefQ.refetch()
    } catch (e) { setErr(e.message) }
    setAssigning(null)
  }

  // A topic with no lesson yet: write it, attach it to the topic so every
  // learner of this language gets the same one, then set it. Uses exactly the
  // builder the learner's Skill Tree uses, so the homework is the tree lesson
  // rather than a teacher-only variant.
  const createAndAssign = async (topic) => {
    setMsg(''); setErr(''); setAssigning(topic.id)
    setBuilding('Writing the lesson…')
    try {
      // The lesson that fits the student's own language — written for their
      // language pair if there isn't one yet, and kept for everyone with it.
      const meetingId = await topicLessonFromServer(client, {
        topicId: topic.id, student,
        onProgress: (secs) => setBuilding(`Writing the lesson on the Mac Studio… ${secs}s`),
      })
      setBuilding('Assigning…')
      await assign({ variables: { meetingId, students: [student], due: due || null } })
      setMsg(`Created “${topic.title}” and set it for ${student}${due ? ` · due ${fmtDate(due)}` : ''}.`)
      treeQ.refetch(); briefQ.refetch()
    } catch (e) {
      setErr((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed — the lesson writer needs it.' : (e.message || 'Could not create the lesson.'))
    }
    setBuilding(''); setAssigning(null)
  }

  const langName = languages.find(l => l.code === lang)?.name || lang

  // Where they are, in one line — the ten minutes before a lesson.
  const summary = brief ? [
    `Practised ${sinceLabel(brief.lastPracticedAt)}`,
    brief.dueNow ? `${brief.dueNow} reviews waiting` : 'reviews up to date',
    brief.nextLevel ? `next topic at ${brief.nextLevel}` : null,
    brief.lastHomeworkTitle ? `“${brief.lastHomeworkTitle}” ${STATUS_WORD[brief.lastHomeworkStatus] || brief.lastHomeworkStatus}` : 'no homework set yet',
  ].filter(Boolean).join(' · ') : ''
  const levels = byLevel.map(([lv]) => lv)
  const shownLevel = treeLevel && levels.includes(treeLevel) ? treeLevel
    : (byLevel.find(([, list]) => list.some(t => !t.completed && !locked(t))) || byLevel[0] || [null])[0]
  const levelTopics = (byLevel.find(([lv]) => lv === shownLevel) || [null, []])[1]
  const weakShown = allWeak ? weak : weak.slice(0, 6)
  const toTree = () => document.getElementById('dashTree')?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  return ReactDOM.createPortal(
    <div className={`${styles.wrap} ${styles[theme]}`}>
      <div className={styles.inner}>
        <div className={styles.top}>
          <div className={styles.who}>
            <span className={styles.avatar} aria-hidden>{(student || '?')[0].toUpperCase()}</span>
            <div>
              <div className={styles.brand}>Student</div>
              <h1 className={styles.name}>{student}</h1>
            </div>
          </div>
          <div className={styles.topRight}>
            <button className={styles.roundBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} aria-label="Toggle light or dark mode">
              {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </button>
            <button className={styles.roundBtn} onClick={onClose} aria-label="Close"><CloseIcon /></button>
          </div>
        </div>

        {backendOld && <div className={styles.err}>The GraphQL server hasn't been redeployed with the teaching queries yet.</div>}
        {denied && <div className={styles.err}>{denied.message}</div>}

        {!denied && !backendOld && (
          <>
            {languages.length > 0 && (
              <div className={styles.langRow}>
                {languages.map(l => (
                  <button key={l.code} className={`${styles.langChip} ${l.code === lang ? styles.langOn : ''}`} onClick={() => setLang(l.code)}>
                    {l.name || l.code}{l.level ? <span className={styles.lvl}>{l.level}</span> : null}
                  </button>
                ))}
              </div>
            )}
            {languages.length === 0 && !langsQ.loading && (
              <div className={`${styles.card} ${styles.empty}`}>
                {student} has not said which language you teach them, so nothing is shared yet. Ask them to
                tick it under <strong>Homework → My teachers</strong>. You will then see their progress in
                that language only.
              </div>
            )}

            {/* ── before your lesson: where they are, and the next step ── */}
            {brief && (
              <section className={`${styles.card} ${styles.hero}`}>
                <div className={styles.cardLabel}>Before your lesson · {langName}</div>
                <div className={styles.heroBody}>
                  <div className={styles.heroText}>{summary}</div>
                  <div className={styles.heroActions}>
                    <button className={styles.primary} onClick={toTree}>Set a lesson</button>
                    <button className={styles.glassBtn} onClick={onOpenImport}>Upload a document</button>
                    <label className={styles.dueLabel}>Due
                      <input type="date" className={styles.dueInput} value={due} onChange={e => setDue(e.target.value)} />
                    </label>
                  </div>
                </div>
                {(building || msg || err) && (
                  <div className={styles.heroMsg}>
                    {building && <span className={styles.building}>{building}</span>}
                    {!building && msg && <span className={styles.msg}>{msg}</span>}
                    {err && <span className={styles.err}>{err}</span>}
                  </div>
                )}
              </section>
            )}

            {brief && (
              <div className={styles.stats}>
                <div className={styles.stat}><b>{sinceLabel(brief.lastPracticedAt)}</b><span>last practised · {brief.itemsPracticed} {brief.itemsPracticed === 1 ? 'item' : 'items'}</span></div>
                <div className={`${styles.stat} ${brief.dueNow > 40 ? styles.statHot : ''}`}><b>{brief.dueNow}</b><span>{brief.dueNow === 0 ? 'nothing to review' : 'due for review'}</span></div>
                <div className={styles.stat}><b>{brief.topicsCompleted}<small> / {brief.topicsTotal}</small></b><span>Skill Tree topics done</span></div>
                <div className={styles.stat}><b>{brief.homeworkSubmitted}<small> / {brief.homeworkAssigned}</small></b><span>homework handed in</span></div>
              </div>
            )}

            <div className={styles.columns}>
              <div className={styles.col}>
                {/* ── what they asked about while practising ── */}
                <PhraseQuestions student={student} lang={lang} styles={styles} />
                {/* ── what they keep getting wrong ── */}
                <section className={styles.card}>
                  <div className={styles.cardHead}><span className={styles.cardLabel}>Getting wrong</span><span className={styles.cardNote}>worst first</span></div>
                  {weakQ.loading && weak.length === 0 ? <div className={styles.muted}>Loading…</div>
                    : weak.length === 0 ? <div className={styles.muted}>Nothing missed in {langName} yet — or nothing practised.</div>
                      : (
                        <div className={styles.weakList}>
                          {weakShown.map(w => (
                            <div key={w.id} className={styles.weakRow}>
                              <div className={styles.weakText}>
                                <div className={styles.weakTerm}>{w.text}</div>
                                {w.translationText && <div className={styles.weakTrans}>{w.translationText}</div>}
                              </div>
                              <div className={styles.weakMeta}>
                                <span className={styles.weakPct} style={{ color: pctColor(w.pct) }}>{w.pct}%</span>
                                <span className={styles.weakCount}>{w.timesCorrect}/{w.timesPracticed} · {sinceLabel(w.lastPracticedAt)}</span>
                              </div>
                            </div>
                          ))}
                          {weak.length > 6 && <button className={styles.link} onClick={() => setAllWeak(a => !a)}>{allWeak ? 'Show fewer' : `Show all ${weak.length} →`}</button>}
                        </div>
                      )}
                </section>

                {/* ── practice they sent you ── */}
                <section className={styles.card}>
                  <div className={styles.cardHead}><span className={styles.cardLabel}>Practice they shared</span></div>
                  {drills.length === 0
                    ? <div className={styles.muted}>Nothing shared yet. They can send a finished set from Special Mode or Verb Lab.</div>
                    : drills.map(d => (
                      <div key={d.id} className={styles.drillRow} onClick={() => setOpenDrill(o => (o === d.id ? null : d.id))}>
                        <div className={styles.drillTop}>
                          <span className={styles.drillTitle}>{d.title}{d.mode ? <span className={styles.drillWhen}> · {drillModeLabel(d.mode)}</span> : null}</span>
                          <span className={styles.drillPct} style={{ color: pctColor(d.pct) }}>{d.pct}%</span>
                        </div>
                        <div className={styles.drillWhen}>{d.perfect}/{d.total} first time · {fmtDate(d.sharedAt)} {openDrill === d.id ? '▾' : '▸'}</div>
                        {d.comment && <div className={styles.comment}>💬 {d.comment}</div>}
                        {openDrill === d.id && (
                          <div style={{ marginTop: '0.6rem', cursor: 'default' }} onClick={e => e.stopPropagation()}>
                            <DrillMisses details={d.details} />
                          </div>
                        )}
                      </div>
                    ))}
                </section>
              </div>

              <div className={styles.col}>
                <section className={styles.card}>
                  <StudentNotes student={student} lang={lang} langName={langName} styles={styles} part="notes" />
                </section>
                <section className={styles.card}>
                  <StudentNotes student={student} lang={lang} langName={langName} styles={styles} part="say" sayLimit={5} />
                </section>
                {/* ── the alphabet board, when the language has one ── */}
                {letters.length > 0 && (
                  <section className={styles.card}>
                    <div className={styles.cardHead}><span className={styles.cardLabel}>Letters</span><span className={styles.cardNote}>amber keeps going wrong</span></div>
                    <div className={styles.letterGrid}>
                      {[...letters]
                        .sort((a, b) => (b.wrong / Math.max(1, b.right + b.wrong)) - (a.wrong / Math.max(1, a.right + a.wrong)))
                        .map(l => {
                          const total = l.right + l.wrong
                          const acc = total ? l.right / total : 0
                          const weakLetter = total >= 2 && acc < 0.7
                          const strong = total >= 3 && acc >= 0.9
                          return (
                            <div key={l.letter} className={`${styles.letter} ${weakLetter ? styles.letterWeak : strong ? styles.letterStrong : ''}`}>
                              <div className={styles.letterChar}>{l.letter}</div>
                              <div className={styles.letterScore}>{l.right}/{total}</div>
                            </div>
                          )
                        })}
                    </div>
                  </section>
                )}
              </div>
            </div>

            {/* ── their tree, a level at a time, with one-tap assigning ── */}
            <section className={styles.treeSection} id="dashTree">
              <div className={styles.cardHead}>
                <span className={styles.cardLabel}>Their Skill Tree</span>
                <span className={styles.cardNote}>what {student} sees in {langName} · set any topic as homework</span>
              </div>
              {topics.length === 0
                ? <div className={`${styles.card} ${styles.muted}`}>No tree has been seeded for {langName} yet.</div>
                : (
                  <>
                    <div className={styles.levelTabs}>
                      {byLevel.map(([level, list]) => (
                        <button key={level} className={`${styles.levelTab} ${level === shownLevel ? styles.levelOn : ''}`} onClick={() => setTreeLevel(level)}>
                          {level}<span>{list.filter(t => t.completed).length}/{list.length}</span>
                        </button>
                      ))}
                    </div>
                    <div className={styles.topicGrid}>
                      {levelTopics.map(t => {
                        const state = t.completed ? 'done' : locked(t) ? 'locked' : 'open'
                        return (
                          <div key={t.id} className={`${styles.topic} ${styles['topic_' + state]}`}>
                            <span className={styles.topicState}>{state === 'done' ? '✓ Done' : state === 'locked' ? 'Locked' : 'Open'}</span>
                            <div className={styles.topicTitle}>{t.title}</div>
                            {t.blurb && <div className={styles.topicBlurb}>{t.blurb}</div>}
                            <div className={styles.topicFoot}>
                              {t.lessonMeetingId
                                ? <button className={styles.assignBtn} onClick={() => doAssign(t)} disabled={!!assigning}>{assigning === t.id ? 'Assigning…' : 'Assign'}</button>
                                : <button className={styles.makeBtn} onClick={() => createAndAssign(t)} disabled={!!assigning} title="No lesson exists for this topic yet — write one and set it">{assigning === t.id ? 'Writing…' : '+ Create & assign'}</button>}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}
            </section>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}
